import React, { createContext, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from './AuthContext'

// Read-only mirror of a student's portal for staff review. The active URL
// determines which student is being viewed:
//   /view-as/:studentId/...  →  staff is viewing that student
//   anything else            →  the auth user is viewing themselves
//
// The mirror is read-only — write actions are disabled in the UI and would
// be rejected by RLS in any case (student-write policies require the row's
// student_id to match the auth user's own student_profile).

interface ViewerStudent {
  studentId:       string | null   // student_profile.student_id of the row to display
  viewerUserId:    string | null   // auth user_id of that student (for storage paths)
  viewerIsStudent: boolean         // true when the auth user IS the student
  /* A parent reading their own child's work. Neither the student nor staff,
     and the difference matters in both directions: they must not see the
     mentor-only abstract, and they must not be mistaken for the student and
     sent looking for a student_profile they do not have. */
  viewerIsParent:  boolean
  studentName:     string | null   // display name for the banner; null when not viewing
  loading:         boolean
  exit:            () => void
  // Prepends /view-as/:studentId when in view-as mode so internal links stay
  // inside the mirror. No-op for regular student sessions.
  prefixPath:      (path: string) => string
}

const ViewAsContext = createContext<ViewerStudent>({
  studentId: null, viewerUserId: null, viewerIsStudent: true, viewerIsParent: false,
  studentName: null, loading: true, exit: () => {},
  prefixPath: (p) => p,
})

const STAFF_ROLES = new Set(['admin', 'office_manager', 'instructor', 'mentor'])

export function ViewAsProvider({ children }: { children: React.ReactNode }) {
  const { session, role, loading: authLoading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const match = location.pathname.match(/^\/view-as\/([^/]+)/)
  const routeStudentId = match?.[1] ?? null
  const isStaff = role ? STAFF_ROLES.has(role) : false
  /* A parent reaches the same /view-as/:studentId URL, but only for a student
     they are linked to — the link is checked below, and RLS refuses the rows
     regardless if they are not. */
  const isParentViewing = !!routeStudentId && role === 'parent'
  const isViewing = !!routeStudentId && (isStaff || isParentViewing)

  const [studentId,    setStudentId]    = useState<string | null>(null)
  const [viewerUserId, setViewerUserId] = useState<string | null>(null)
  const [studentName,  setStudentName]  = useState<string | null>(null)
  const [loading,      setLoading]      = useState(true)

  useEffect(() => {
    let cancelled = false
    async function resolve() {
      if (authLoading) return
      if (!session) {
        setStudentId(null); setViewerUserId(null); setStudentName(null); setLoading(false); return
      }
      // Wait for the profile fetch to populate role. Without this we'd
      // briefly compute isViewing=false (since the staff check fails on
      // role=null) and write studentId=null + loading=false to consumers,
      // which trips downstream "no profile" branches before role arrives.
      if (!role) return
      setLoading(true)

      if (isViewing && routeStudentId) {
        /* A parent may only view a student they are linked to. RLS would
           refuse the work rows anyway, but checking here means they get an
           empty state rather than a half-rendered page. */
        if (isParentViewing) {
          const { data: link } = await supabase
            .from('parent_student_link')
            .select('student_id')
            .eq('parent_user_id', session.user.id)
            .eq('student_id', routeStudentId)
            .maybeSingle()
          if (cancelled) return
          if (!link) {
            setStudentId(null); setViewerUserId(null); setStudentName(null); setLoading(false); return
          }
        }
        const { data: sp } = await supabase
          .from('student_profile')
          .select('student_id, user_id')
          .eq('student_id', routeStudentId)
          .maybeSingle()
        if (cancelled) return
        if (!sp) {
          setStudentId(null); setViewerUserId(null); setStudentName(null); setLoading(false); return
        }
        setStudentId(sp.student_id)
        setViewerUserId(sp.user_id)
        const { data: u } = await supabase
          .from('users')
          .select('first_name, last_name, email')
          .eq('user_id', sp.user_id)
          .maybeSingle()
        if (cancelled) return
        const display = u
          ? (`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email)
          : null
        setStudentName(display)

        /* Best-effort audit log; ignore errors so reviewers aren't blocked.
           A parent reading their own child is not impersonation, so it is not
           logged as such. */
        if (!isParentViewing) {
          await supabase.from('impersonation_log').insert({
            staff_id:   session.user.id,
            student_id: sp.student_id,
            context:    'view_as',
          })
        }
      } else if (role === 'student') {
        const { data: sp } = await supabase
          .from('student_profile')
          .select('student_id, user_id')
          .eq('user_id', session.user.id)
          .maybeSingle()
        if (cancelled) return
        setStudentId(sp?.student_id ?? null)
        setViewerUserId(session.user.id)
        setStudentName(null)
      } else {
        setStudentId(null); setViewerUserId(null); setStudentName(null)
      }
      setLoading(false)
    }
    resolve()
    return () => { cancelled = true }
    // We log on each new (staff, student) pair entered via routeStudentId
    // changing. Re-resolving when role changes is also intentional.
    /* isParentViewing is listed even though it moves with isViewing today:
       the link check and the audit-log skip both branch on it, and a stale
       closure there would silently skip a permission check. */
  }, [session, role, routeStudentId, isViewing, isParentViewing, authLoading])

  function exit() {
    // Entry buttons open view-as in a new tab via window.open(); in that
    // case window.close() lands the staffer back on their original tab.
    // If the URL was opened directly (no opener), navigate to /students.
    if (window.opener) {
      window.close()
    } else {
      navigate('/students')
    }
  }

  function prefixPath(path: string) {
    if (!isViewing || !routeStudentId) return path
    const clean = path.startsWith('/') ? path : `/${path}`
    return `/view-as/${routeStudentId}${clean}`
  }

  return (
    <ViewAsContext.Provider value={{
      studentId,
      viewerUserId,
      /* A parent counts as "not staff" for everything the student flag gates —
         chiefly the mentor-only abstract, which they must not see on the site
         because it is what the email already gave them. */
      viewerIsStudent: !isViewing || isParentViewing,
      viewerIsParent:  isParentViewing,
      studentName,
      loading,
      exit,
      prefixPath,
    }}>
      {children}
    </ViewAsContext.Provider>
  )
}

export function useViewerStudent() {
  return useContext(ViewAsContext)
}
